import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  gregorianToEth,
  ethToGregorian,
  ETHIOPIAN_MONTH_OPTIONS,
} from '../utils/ethiopianCalendar';

// ── UTC-safe helpers ───────────────────────────────────────────
const parseGcDate = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

const gcToStr = (utcDate) => {
  if (!utcDate) return "";
  const y  = utcDate.getUTCFullYear();
  const mo = String(utcDate.getUTCMonth() + 1).padStart(2, "0");
  const d  = String(utcDate.getUTCDate()).padStart(2, "0");
  return `${y}-${mo}-${d}`;
};

export const todayGcStr = () => {
  const now = new Date();
  return gcToStr(new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate()
  )));
};

const daysInEthMonth = (month, year) => {
  if (month === 13) return year % 4 === 3 ? 6 : 5;
  return 30;
};

// ── Shared select style ────────────────────────────────────────
const selStyle = (width) => ({
  border:       "1px solid #ced4da",
  borderRadius: "0.375rem",
  padding:      "6px 8px",
  fontSize:     "1.3rem",
  background:   "white",
  cursor:       "pointer",
  height:       "46px",
  width,
});

// ── Year options helper ────────────────────────────────────────
const buildYearOptions = (todayEth) => {
  const years = [];
  for (let y = todayEth.year + 1; y >= todayEth.year - 10; y--) {
    years.push(y);
  }
  return years;
};

/**
 * DualDateInput
 * EC on left, GC on right — synced.
 * Used in Reports custom range (side by side layout).
 *
 * @param {string}   label     - field label
 * @param {string}   value     - current value as GC "YYYY-MM-DD"
 * @param {function} onChange  - callback(gcString)
 * @param {string}   maxDate   - max GC date "YYYY-MM-DD"
 * @param {string}   minDate   - min GC date "YYYY-MM-DD"
 * @param {boolean}  required
 */
export const DualDateInput = ({
  label,
  value,
  onChange,
  maxDate,
  minDate,
  required = false,
}) => {
  const today = todayGcStr();
  const max   = maxDate || today;

  const todayEth    = useMemo(() => gregorianToEth(parseGcDate(today)), []);
  const yearOptions = useMemo(() => buildYearOptions(todayEth), [todayEth]);

  // ── Local EC state — owned here, not derived from prop ────
  const initEth = useMemo(() => {
    if (!value) return todayEth;
    try { return gregorianToEth(parseGcDate(value)); }
    catch { return todayEth; }
  }, []); // ✅ mount only — intentionally no deps

  const [ecDay,   setEcDay]   = useState(initEth.day);
  const [ecMonth, setEcMonth] = useState(initEth.month);
  const [ecYear,  setEcYear]  = useState(initEth.year);

  // ✅ Prevent useEffect from overriding local state after EC change
  const skipSync = useRef(false);

  // Sync EC dropdowns when value changes FROM OUTSIDE (GC input or parent)
  useEffect(() => {
    if (skipSync.current) { skipSync.current = false; return; }
    if (!value) return;
    try {
      const eth = gregorianToEth(parseGcDate(value));
      setEcDay(eth.day);
      setEcMonth(eth.month);
      setEcYear(eth.year);
    } catch {}
  }, [value]);

  // GC equivalent shown below GC input
  const gcEthLabel = useMemo(() => {
    if (!value) return null;
    try { return gregorianToEth(parseGcDate(value)); }
    catch { return null; }
  }, [value]);

  // ── EC dropdown change ────────────────────────────────────
  const handleEthChange = (day, month, year) => {
    // ✅ Update local state immediately — don't wait for round-trip
    setEcDay(day);
    setEcMonth(month);
    setEcYear(year);
    skipSync.current = true; // ✅ skip next useEffect sync

    const safeDay = Math.min(day, daysInEthMonth(month, year));
    try {
      const gcDate = ethToGregorian(year, month, safeDay);
      const gcStr  = gcToStr(gcDate);
      if (minDate && gcStr < minDate) return;
      if (gcStr > max) return;
      onChange(gcStr);
    } catch (e) { console.warn("EC→GC:", e); }
  };

  // ── GC input change ───────────────────────────────────────
  const handleGcChange = (gcStr) => {
    if (!gcStr) { onChange(""); return; }
    if (minDate && gcStr < minDate) return;
    if (gcStr > max) return;
    // Sync EC dropdowns to match new GC value
    try {
      const eth = gregorianToEth(parseGcDate(gcStr));
      skipSync.current = true;
      setEcDay(eth.day);
      setEcMonth(eth.month);
      setEcYear(eth.year);
    } catch {}
    onChange(gcStr);
  };

  return (
    <div>
      {label && (
        <label className="fs-4 fw-medium mb-2 d-block">{label}</label>
      )}
      <div className="row g-2">

        {/* ── EC — left ── */}
        <div className="col-12 col-md-6">
          <div className="border rounded-3 px-3 py-2 bg-white h-100">
            <p className="text-muted mb-2 fs-5 fw-medium">
              🇪🇹 Ethiopian Calendar
            </p>
            <div className="d-flex gap-2 flex-wrap">

              {/* Day */}
              <div className="d-flex flex-column">
                <span className="text-muted mb-1 fs-5">Day</span>
                <select
                  value={ecDay}
                  style={selStyle("65px")}
                  onChange={(e) =>
                    handleEthChange(Number(e.target.value), ecMonth, ecYear)
                  }>
                  {Array.from(
                    { length: daysInEthMonth(ecMonth, ecYear) },
                    (_, i) => i + 1
                  ).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              {/* Month */}
              <div className="d-flex flex-column">
                <span className="text-muted mb-1 fs-5">
                  Month
                </span>
                <select
                  value={ecMonth}
                  style={selStyle("130px")}
                  onChange={(e) =>
                    handleEthChange(ecDay, Number(e.target.value), ecYear)
                  }>
                  {ETHIOPIAN_MONTH_OPTIONS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              {/* Year */}
              <div className="d-flex flex-column">
                <span className="text-muted mb-1 fs-5">
                  Year (E.C.)
                </span>
                <select
                  value={ecYear}
                  style={selStyle("105px")}
                  onChange={(e) =>
                    handleEthChange(ecDay, ecMonth, Number(e.target.value))
                  }>
                  {yearOptions.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

            </div>
          </div>
        </div>

        {/* ── GC — right ── */}
        <div className="col-12 col-md-6">
          <div className="border rounded-3 px-3 py-2 bg-white h-100">
            <p className="text-muted mb-2 fs-5 fw-medium">
              📅 Gregorian Calendar
            </p>
            <input
              type="date"
              className="form-control form-control-lg fs-4"
              style={{ height: "46px" }}
              value={value || ""}
              min={minDate || ""}
              max={max}
              required={required}
              onChange={(e) => handleGcChange(e.target.value)}
            />
            {value && gcEthLabel && (
              <small className="text-primary fs-5 mt-1 d-block">
                = {gcEthLabel.monthName} {gcEthLabel.day},{" "}
                {gcEthLabel.year} E.C.
              </small>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

/**
 * DualDateRangeInput
 * Start and End in separate rows, each with EC left + GC right.
 * Optional centered generate button below.
 *
 * @param {string}   startDate
 * @param {string}   endDate
 * @param {function} onStartChange
 * @param {function} onEndChange
 * @param {boolean}  required
 * @param {boolean}  showButton    - show generate button
 * @param {function} onSubmit      - button click handler
 * @param {boolean}  isLoading     - shows spinner on button
 */
export const DualDateRangeInput = ({
  startDate,
  endDate,
  onStartChange,
  onEndChange,
  required    = false,
  showButton  = false,
  onSubmit,
  isLoading   = false,
}) => {
  const today = todayGcStr();

  // ✅ Always have a value so neither input is ever empty
  const effectiveStart = startDate || today;
  const effectiveEnd   = endDate   || today;

  return (
    <div className="d-flex flex-column gap-3">

      <DualDateInput
        label="Start Date"
        value={effectiveStart}
        onChange={onStartChange}
        maxDate={today}           // ✅ max is today, not endDate
        required={required}
      />

      <DualDateInput
        label="End Date"
        value={effectiveEnd}
        onChange={onEndChange}
        minDate={effectiveStart}  // ✅ cannot be before start
        maxDate={today}
        required={required}
      />

      {/* ✅ Centered generate button */}
      {showButton && onSubmit && (
        <div className="d-flex justify-content-center mt-2">
          <button
            type="button"         // ✅ not type="submit" — no form needed
            className="btn btn-primary btn-lg rounded-3 px-5 fs-4"
            onClick={onSubmit}
            disabled={isLoading}>
            {isLoading ? (
              <span>
                <span className="spinner-border spinner-border-sm me-2" />
                Generating...
              </span>
            ) : "Generate Report"}
          </button>
        </div>
      )}

    </div>
  );
};

/**
 * SimpleDualDateInput
 * EC on top, GC on bottom. No button.
 * Used in all other components: sales, batches, adjustments, returns etc.
 * Can be left empty (empty = use current date on backend).
 *
 * @param {string}   label
 * @param {string}   value        - GC "YYYY-MM-DD" or ""
 * @param {function} onChange     - callback(gcString or "")
 * @param {string}   maxDate
 * @param {string}   minDate
 * @param {string}   placeholder  - shown when empty
 */
export const SimpleDualDateInput = ({
  label,
  value,
  onChange,
  maxDate,
  minDate,
  placeholder = "Leave empty for current date",
}) => {
  const today = todayGcStr();
  const max   = maxDate || today;

  const todayEth    = useMemo(() => gregorianToEth(parseGcDate(today)), []);
  const yearOptions = useMemo(() => buildYearOptions(todayEth), [todayEth]);

  // ── Local EC state — owned here ───────────────────────────
  const initEth = useMemo(() => {
    if (!value) return todayEth;
    try { return gregorianToEth(parseGcDate(value)); }
    catch { return todayEth; }
  }, []); // ✅ mount only

  const [ecDay,    setEcDay]    = useState(initEth.day);
  const [ecMonth,  setEcMonth]  = useState(initEth.month);
  const [ecYear,   setEcYear]   = useState(initEth.year);
  const [lastUsed, setLastUsed] = useState("none");

  const skipSync = useRef(false);

  // Sync EC when value changes from outside
  useEffect(() => {
    if (skipSync.current) { skipSync.current = false; return; }
    if (!value) return;
    try {
      const eth = gregorianToEth(parseGcDate(value));
      setEcDay(eth.day);
      setEcMonth(eth.month);
      setEcYear(eth.year);
    } catch {}
  }, [value]);

  // GC equivalent for display under GC input
  const gcEthLabel = useMemo(() => {
    if (!value) return null;
    try { return gregorianToEth(parseGcDate(value)); }
    catch { return null; }
  }, [value]);

  // ── EC dropdown change ────────────────────────────────────
  const handleEthChange = (day, month, year) => {
    setLastUsed("ec");
    setEcDay(day);      // ✅ update immediately
    setEcMonth(month);
    setEcYear(year);
    skipSync.current = true;

    const safeDay = Math.min(day, daysInEthMonth(month, year));
    try {
      const gcDate = ethToGregorian(year, month, safeDay);
      const gcStr  = gcToStr(gcDate);
      if (minDate && gcStr < minDate) return;
      if (gcStr > max) return;
      onChange(gcStr);
    } catch (e) { console.warn("EC→GC:", e); }
  };

  // ── GC input change ───────────────────────────────────────
  const handleGcChange = (gcStr) => {
    setLastUsed("gc");
    if (!gcStr) { onChange(""); return; }
    if (minDate && gcStr < minDate) return;
    if (gcStr > max) return;
    try {
      const eth = gregorianToEth(parseGcDate(gcStr));
      skipSync.current = true;
      setEcDay(eth.day);
      setEcMonth(eth.month);
      setEcYear(eth.year);
    } catch {}
    onChange(gcStr);
  };

  const simpleSelStyle = (width) => ({
    border:       "1px solid #ced4da",
    borderRadius: "0.375rem",
    padding:      "4px 8px",
    fontSize:     "1.3rem",
    background:   "white",
    cursor:       "pointer",
    height:       "42px",
    width,
  });

  console.log("ok");

  return (
    <div>
      {label && (
        <label className="fs-4 fw-medium mb-2 d-block">{label}</label>
      )}

      {/* ── EC — top ── */}
      <div className="border rounded-3 px-3 py-2 bg-white mb-2">
        <p className="text-muted mb-2 fs-5 fw-medium">
          🇪🇹 Ethiopian Calendar
        </p>
        <div className="d-flex gap-2 flex-wrap align-items-center">

          {/* Day */}
          <div className="d-flex flex-column">
            <span className="text-muted mb-1 fs-5"
              >Day</span>
            <select
              value={ecDay}
              style={simpleSelStyle("62px")}
              onChange={(e) =>
                handleEthChange(Number(e.target.value), ecMonth, ecYear)
              }>
              {Array.from(
                { length: daysInEthMonth(ecMonth, ecYear) },
                (_, i) => i + 1
              ).map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          {/* Month */}
          <div className="d-flex flex-column">
            <span className="text-muted mb-1 fs-5"
              >Month</span>
            <select
              value={ecMonth}
              style={simpleSelStyle("125px")}
              onChange={(e) =>
                handleEthChange(ecDay, Number(e.target.value), ecYear)
              }>
              {ETHIOPIAN_MONTH_OPTIONS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          {/* Year */}
          <div className="d-flex flex-column">
            <span className="text-muted mb-1 fs-5">
              Year (E.C.)
            </span>
            <select
              value={ecYear}
              style={simpleSelStyle("100px")}
              onChange={(e) =>
                handleEthChange(ecDay, ecMonth, Number(e.target.value))
              }>
              {yearOptions.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {/* Show GC equivalent when EC was last used */}
          {lastUsed === "ec" && value && (
            <span className="badge bg-primary bg-opacity-25
                             text-primary rounded-3 px-2 py-2 fs-5 mt-auto">
              = {value}
            </span>
          )}

        </div>
      </div>

      {/* ── GC — bottom ── */}
      <div className="border rounded-3 px-3 py-2 bg-white">
        <p className="text-muted mb-2 fs-5 fw-medium">
          📅 Gregorian Calendar
        </p>
        <input
          type="date"
          className="form-control form-control-lg fs-4"
          value={value || ""}
          min={minDate || ""}
          max={max}
          onChange={(e) => handleGcChange(e.target.value)}
        />
        {!value && placeholder && (
          <small className="text-muted fs-5 mt-1 d-block">
            {placeholder}
          </small>
        )}
        {/* Show EC equivalent when GC was last used */}
        {lastUsed === "gc" && value && gcEthLabel && (
          <small className="text-primary fs-5 mt-1 d-block">
            = {gcEthLabel.monthName} {gcEthLabel.day},{" "}
            {gcEthLabel.year} E.C.
          </small>
        )}
      </div>

    </div>
  );
};